import {
  ApiBlueprint,
  BreadcrumbEntry,
  PageBlueprint,
  configApiRef,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { MarketplaceClient, marketplaceApiRef } from './api';
import { Route, Routes, useParams } from 'react-router-dom';
import { MarketplacePage } from './MarketplacePage';
import { PluginPage } from './PluginPage';
import { ItemPage } from './ItemPage';

/**
 * Nests the plugin name as a crumb so the trail reads
 * Marketplace > codeops-core > adr, the same way the scaffolder builds
 * Create › Start › <template>.
 */
const WithPluginCrumb = ({ children }: { children: JSX.Element }) => {
  const { pluginName } = useParams();
  return (
    <BreadcrumbEntry entry={{ label: pluginName ?? 'Plugin', href: '.' }}>
      {children}
    </BreadcrumbEntry>
  );
};

/**
 * The item's own crumb, nested inside the plugin's, so the trail reaches all
 * three levels. `href: '.'` keeps each crumb pointing at its own URL.
 */
const WithItemCrumb = ({ children }: { children: JSX.Element }) => {
  const { pluginName, itemName } = useParams();
  return (
    <BreadcrumbEntry entry={{ label: pluginName ?? 'Plugin', href: '..' }}>
      <BreadcrumbEntry entry={{ label: itemName ?? 'Item', href: '.' }}>
        {children}
      </BreadcrumbEntry>
    </BreadcrumbEntry>
  );
};

/**
 * One shared client for the whole app. The pages used to build their own inside
 * hooks, so nothing could be cached across them — opening a single skill
 * refetched the manifest, the entire repo tree and every sibling item's
 * frontmatter.
 */
const marketplaceApi = ApiBlueprint.make({
  name: 'marketplace',
  params: defineParams =>
    defineParams({
      api: marketplaceApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, fetchApi, configApi }) =>
        new MarketplaceClient(discoveryApi, fetchApi, configApi),
    }),
});

const marketplacePage = PageBlueprint.make({
  name: 'marketplace',
  params: {
    path: '/marketplace',
    loader: async () => (
      <Routes>
        <Route index element={<MarketplacePage />} />
        <Route
          path=":pluginName"
          element={
            <WithPluginCrumb>
              <PluginPage />
            </WithPluginCrumb>
          }
        />
        <Route
          path=":pluginName/:itemKind/:itemName"
          element={
            <WithItemCrumb>
              <ItemPage />
            </WithItemCrumb>
          }
        />
      </Routes>
    ),
  },
});

/**
 * Its own plugin rather than a page on `codeops`, because the plugin title is
 * what PluginHeader renders as the leading breadcrumb — on `codeops` (titled
 * "Catalog") the trail would read "Catalog > codeops-core", which is wrong:
 * the marketplace is a separate surface, not part of the software catalog.
 */
export const marketplacePlugin = createFrontendPlugin({
  pluginId: 'marketplace',
  title: 'Marketplace',
  extensions: [marketplaceApi, marketplacePage],
});
